import React, { useMemo } from "react";
import {
  Box,
  Stack,
  Chip,
  Autocomplete,
  TextField,
  Button,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import SortOutlinedIcon from "@mui/icons-material/SortOutlined";
import { useTranslation } from "react-i18next";
import {
  DUE_FILTERS,
  URGENCY_FILTERS,
  SORT_MODES,
  UNASSIGNED_ASSIGNEE_ID,
  NO_CLIENT_ID,
  hasActiveBoardFilters,
} from "../utils/teamBoardCardFilters";

const fieldSx = {
  minWidth: { xs: "100%", sm: 156 },
  maxWidth: { sm: 200 },
  flex: { xs: "1 1 100%", sm: "0 1 180px" },
  "& .MuiOutlinedInput-root": {
    bgcolor: "background.paper",
    borderRadius: 1.5,
    fontSize: "0.8125rem",
    fontWeight: 500,
  },
  "& .MuiInputBase-input": {
    py: "6px !important",
  },
};

const chipSx = {
  height: 28,
  borderRadius: 1.5,
  fontWeight: 600,
  fontSize: "0.72rem",
  "& .MuiChip-label": { px: 1 },
};

function FilterChip({ active, label, color = "primary", onClick }) {
  return (
    <Chip
      size="small"
      clickable
      label={label}
      color={active ? color : "default"}
      variant={active ? "filled" : "outlined"}
      onClick={onClick}
      sx={{
        ...chipSx,
        borderColor: (th) =>
          active ? undefined : alpha(th.palette.divider, 0.75),
        bgcolor: (th) =>
          active
            ? undefined
            : alpha(th.palette.background.paper, th.palette.mode === "dark" ? 0.4 : 0.9),
      }}
    />
  );
}

const BoardFiltersBar = ({
  filters,
  onChange,
  assigneeOptions = [],
  clientOptions = [],
  compact = false,
  embedded = false,
}) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();

  const unassignedOption = useMemo(
    () => ({ id: UNASSIGNED_ASSIGNEE_ID, nome: t("inboxFilterUnassigned") }),
    [t]
  );
  const noClientOption = useMemo(
    () => ({ id: NO_CLIENT_ID, name: t("boardFilterNoClient") }),
    [t]
  );

  const assignees = useMemo(
    () => [unassignedOption, ...assigneeOptions],
    [unassignedOption, assigneeOptions]
  );
  const clients = useMemo(
    () => [noClientOption, ...clientOptions],
    [noClientOption, clientOptions]
  );

  const selectedAssignee = useMemo(() => {
    if (filters.assigneeId == null) return null;
    return assignees.find((u) => Number(u.id) === Number(filters.assigneeId)) || null;
  }, [assignees, filters.assigneeId]);

  const selectedClient = useMemo(() => {
    if (filters.clientId == null) return null;
    return clients.find((c) => Number(c.id) === Number(filters.clientId)) || null;
  }, [clients, filters.clientId]);

  const active = hasActiveBoardFilters(filters);
  const sortByDue = filters.sort === SORT_MODES.DUE;

  const dueChips = [
    { key: DUE_FILTERS.OVERDUE, label: t("boardFilterDueOverdue"), color: "warning" },
    { key: DUE_FILTERS.TODAY, label: t("boardFilterDueToday") },
    { key: DUE_FILTERS.WEEK, label: t("boardFilterDueWeek") },
    { key: DUE_FILTERS.NONE, label: t("boardFilterDueNone") },
  ];

  const patch = (partial) => onChange?.({ ...filters, ...partial });

  const clearAll = () =>
    onChange?.({
      assigneeId: null,
      due: null,
      urgency: null,
      clientId: null,
      sort: SORT_MODES.BOARD,
    });

  return (
    <Box
      sx={{
        flexShrink: 0,
        px: compact || embedded ? 1.25 : 2,
        py: 0.75,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
        bgcolor: alpha(
          theme.palette.background.paper,
          theme.palette.mode === "dark" ? 0.72 : 0.9
        ),
        backdropFilter: "blur(14px)",
        boxShadow: (th) =>
          th.palette.mode === "dark"
            ? `0 4px 16px ${alpha(th.palette.common.black, 0.2)}`
            : `0 1px 0 ${alpha(th.palette.divider, 0.55)}, 0 4px 12px ${alpha(th.palette.common.black, 0.03)}`,
        position: "relative",
        zIndex: 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ width: "100%", minWidth: 0, rowGap: 0.75 }}
      >
        <Tooltip title={t("boardFiltersHint")}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              display: { xs: "none", md: "inline-flex" },
              alignItems: "center",
              justifyContent: "center",
              color: active ? "primary.main" : "text.secondary",
              bgcolor: active
                ? alpha(theme.palette.primary.main, 0.1)
                : alpha(theme.palette.action.active, 0.05),
              flexShrink: 0,
            }}
            aria-hidden
          >
            <FilterAltOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>

        <Autocomplete
          size="small"
          options={assignees}
          value={selectedAssignee}
          onChange={(_, user) =>
            patch({ assigneeId: user?.id != null ? Number(user.id) : null })
          }
          getOptionLabel={(u) => u?.nome || u?.name || u?.email || ""}
          isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("boardFilterAssigneePlaceholder")}
              inputProps={{
                ...params.inputProps,
                "aria-label": t("inboxFilterAssignee"),
              }}
            />
          )}
          sx={fieldSx}
        />

        <Autocomplete
          size="small"
          options={clients}
          value={selectedClient}
          onChange={(_, client) =>
            patch({ clientId: client?.id != null ? Number(client.id) : null })
          }
          getOptionLabel={(c) => c?.name || ""}
          isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("boardFilterClientPlaceholder")}
              inputProps={{
                ...params.inputProps,
                "aria-label": t("boardFilterClient"),
              }}
            />
          )}
          sx={fieldSx}
          noOptionsText={t("boardFilterClientEmpty")}
        />

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            display: { xs: "none", sm: "block" },
            mx: 0.25,
            borderColor: alpha(theme.palette.divider, 0.7),
          }}
        />

        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
          {dueChips.map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              color={chip.color || "primary"}
              active={filters.due === chip.key}
              onClick={() => patch({ due: filters.due === chip.key ? null : chip.key })}
            />
          ))}
        </Stack>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            display: { xs: "none", md: "block" },
            mx: 0.25,
            borderColor: alpha(theme.palette.divider, 0.7),
          }}
        />

        <ToggleButtonGroup
          exclusive
          size="small"
          value={filters.urgency || null}
          onChange={(_, value) => patch({ urgency: value || null })}
          sx={{
            flexShrink: 0,
            "& .MuiToggleButton-root": {
              px: 1.1,
              py: 0.35,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.72rem",
              borderRadius: "6px !important",
              borderColor: alpha(theme.palette.divider, 0.75),
              color: "text.secondary",
              "&.Mui-selected": {
                bgcolor: alpha(theme.palette.warning.main, 0.14),
                color: theme.palette.mode === "dark" ? "warning.light" : "warning.dark",
                borderColor: alpha(theme.palette.warning.main, 0.35),
                "&:hover": {
                  bgcolor: alpha(theme.palette.warning.main, 0.2),
                },
              },
            },
          }}
        >
          <ToggleButton value={URGENCY_FILTERS.REQUIRED} aria-label={t("boardFilterUrgencyRequired")}>
            {t("boardFilterUrgencyRequired")}
          </ToggleButton>
          <ToggleButton value={URGENCY_FILTERS.NORMAL} aria-label={t("boardFilterUrgencyNormal")}>
            {t("boardFilterUrgencyNormal")}
          </ToggleButton>
        </ToggleButtonGroup>

        <Tooltip
          title={
            sortByDue
              ? t("boardFilterSortByDueActiveHint")
              : t("boardFilterSortByDueHint")
          }
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <ToggleButton
              size="small"
              value="due"
              selected={sortByDue}
              onChange={() =>
                patch({
                  sort: sortByDue ? SORT_MODES.BOARD : SORT_MODES.DUE,
                })
              }
              sx={{
                px: 1.1,
                py: 0.35,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.72rem",
                borderRadius: 1.5,
                borderColor: alpha(theme.palette.divider, 0.75),
                color: sortByDue ? "primary.main" : "text.secondary",
                gap: 0.5,
                "&.Mui-selected": {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                },
              }}
            >
              <SortOutlinedIcon sx={{ fontSize: 15 }} />
              {t("boardFilterSortByDue")}
            </ToggleButton>
          </Box>
        </Tooltip>

        <Box sx={{ flex: { sm: "1 1 auto" }, minWidth: 0, display: { xs: "none", sm: "block" } }} />

        {active ? (
          <Tooltip title={t("boardFiltersDragDisabledHint")}>
            <Button
              size="small"
              color="inherit"
              startIcon={<FilterAltOffOutlinedIcon />}
              onClick={clearAll}
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "text.secondary",
                borderRadius: 1.5,
                px: 1,
                minWidth: 0,
                ml: { xs: "auto", sm: 0 },
                "&:hover": {
                  bgcolor: alpha(theme.palette.action.active, 0.08),
                  color: "text.primary",
                },
              }}
            >
              {t("boardFiltersClear")}
            </Button>
          </Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
};

export default BoardFiltersBar;
